import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OperationSystemOption } from '../../../../../components/Atoms';
import type {
    LaunchBaremetalInstanceData,
    XVerBaremetalPackageRender,
} from '../../../../../@types/launch';
import { imageOsLabel, installMinutes, resolveImageLabel } from '../../../../../utils/baremetal';
import { useAvailableBaremetalNodeQuery } from './useAvailableBaremetalNodeQuery';
import { useBaremetalLaunchOptionQuery } from './useBaremetalLaunchOptionQuery';
import { useLaunchBaremetalMutate } from './useLaunchBaremetalMutate';

export type PaymentPeriod = 'weekly' | 'monthly' | 'yearly';

export function useBareMetalComputeDeploymentPage() {
    const { t } = useTranslation('bareMetalDeployment');
    const { data: launchOptions, isPending: isLaunchOptionsPending } =
        useBaremetalLaunchOptionQuery();
    const { data: availableNodes, isPending: isAvailableNodesPending } =
        useAvailableBaremetalNodeQuery();
    const launchMutation = useLaunchBaremetalMutate();

    const [selectedOsSlug, setSelectedOsSlug] = useState<string | null>(null);
    const [selectedVersionSlug, setSelectedVersionSlug] = useState<string | null>(null);
    const [selectedPackageSlug, setSelectedPackageSlug] = useState<string | null>(null);
    const [paymentPeriod, setPaymentPeriod] = useState<PaymentPeriod>('monthly');
    const [hostname, setHostname] = useState('');

    useEffect(() => {
        setHostname(
            (currentHostname) => currentHostname || `xver-${Math.random().toString(36).slice(2, 8)}`
        );
    }, []);

    const osOptions: OperationSystemOption[] = useMemo(() => {
        const images = launchOptions?.images?.images ?? [];
        return [...images]
            .sort((a, b) => a.order - b.order)
            .map((img) => ({
                name: img.label,
                slug: img.slug,
                versions: img.versions.map((v) => ({ name: v.label, slug: v.slug })),
            }));
    }, [launchOptions]);

    const packages: XVerBaremetalPackageRender[] = useMemo(() => {
        if (!launchOptions?.packages || !availableNodes?.available_packages) return [];
        return [...launchOptions.packages]
            .sort((a, b) => b.priority - a.priority)
            .map((pkg) => {
                const node = availableNodes.available_packages.find((n) => n.slug === pkg.slug);
                return {
                    ...pkg,
                    quantity: node?.quantity ?? 0,
                    available: !!node?.available,
                };
            })
            .filter((p) => p.available);
    }, [launchOptions, availableNodes]);

    useEffect(() => {
        if (osOptions.length > 0 && !selectedOsSlug) {
            const firstOs = osOptions[0];
            setSelectedOsSlug(firstOs.slug);
            setSelectedVersionSlug(firstOs.versions[0]?.slug ?? null);
        }
    }, [osOptions, selectedOsSlug]);

    useEffect(() => {
        const isValid = packages.some((p) => p.slug === selectedPackageSlug);
        if ((!selectedPackageSlug || !isValid) && packages.length > 0) {
            setSelectedPackageSlug(packages[0].slug);
        }
    }, [packages, selectedPackageSlug]);

    const selectedOs = osOptions.find((o) => o.slug === selectedOsSlug) ?? null;
    const selectedPackage = packages.find((p) => p.slug === selectedPackageSlug);
    const selectedOsLabel =
        selectedOs && selectedVersionSlug
            ? imageOsLabel(
                  resolveImageLabel(launchOptions?.images, selectedOs.slug, selectedVersionSlug)
              )
            : null;
    const selectedOsInstallMinutes = installMinutes(selectedOs?.slug);
    const selectedOsKind: 'linux' | 'windows' = selectedOs?.slug.toLowerCase().includes('windows')
        ? 'windows'
        : 'linux';

    const hostnameError =
        hostname.length > 0 && hostname.length < 4 ? t('hostname.lengthError') : undefined;

    const isDeployDisabled =
        !selectedOs ||
        !selectedVersionSlug ||
        !selectedPackage ||
        hostname.length < 4 ||
        !!hostnameError;

    const handleHostnameChange = (value: string) => {
        const sanitized = value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20);
        setHostname(sanitized);
    };

    const handleHostnamePaste = (event: any) => {
        event.preventDefault();
        const paste = (event.clipboardData?.getData && event.clipboardData.getData('text')) || '';
        const sanitized = paste.replace(/[^a-zA-Z0-9-]/g, '');
        const input = event.currentTarget as HTMLInputElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const remaining = 20 - (input.value.length - (end - start));
        const toInsert = sanitized.slice(0, Math.max(0, remaining));
        const newVal = input.value.slice(0, start) + toInsert + input.value.slice(end);
        setHostname(newVal);
    };

    const handleGenerateHostname = () => {
        setHostname(`xver-${Math.random().toString(36).slice(2, 8)}`);
    };

    const handleSelectOs = (os: OperationSystemOption) => {
        setSelectedOsSlug(os.slug);
        setSelectedVersionSlug(os.versions[0]?.slug ?? null);
    };

    const handleSelectVersion = (os: OperationSystemOption, versionSlug: string | null) => {
        setSelectedOsSlug(os.slug);
        setSelectedVersionSlug(versionSlug);
    };

    const handleDeploy = () => {
        if (!selectedOs || !selectedVersionSlug || !selectedPackage) return;

        const selectedImage = launchOptions?.images?.images?.find(
            (img) => img.slug === selectedOs.slug
        );
        const selectedVersion = selectedImage?.versions?.find(
            (v) => v.slug === selectedVersionSlug
        );
        const username =
            selectedVersion?.defaultUsername ??
            (selectedOs.slug.toLowerCase().includes('windows') ? 'Administrator' : 'xver');

        const payload: LaunchBaremetalInstanceData = {
            package_id: selectedPackage.id,
            package_slug: selectedPackage.slug,
            image_slug: selectedOs.slug,
            image_version_slug: selectedVersionSlug,
            payment_period: paymentPeriod,
            hostname,
            username,
        };

        launchMutation.mutate(payload);
    };

    return {
        hostname,
        hostnameError,
        isAvailableNodesPending,
        isDeployDisabled,
        isDeployLoading: launchMutation.isPending,
        isLaunchOptionsPending,
        osOptions,
        packages,
        paymentPeriod,
        selectedOs,
        selectedOsKind,
        selectedOsLabel,
        selectedOsInstallMinutes,
        selectedPackage,
        selectedVersionSlug,
        handleDeploy,
        handleGenerateHostname,
        handleHostnameChange,
        handleHostnamePaste,
        handleSelectOs,
        handleSelectVersion,
        setPaymentPeriod,
        setSelectedPackageSlug,
        setSelectedVersionSlug,
    };
}
